import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import type { Conversation, IntakeSummary } from '@/types';

const REVIEW_STATUSES = new Set(['queued', 'claimed', 'needs_info', 'decision_ready', 'blocked']);

const BOOKABILITY_LABEL_KEYS: Record<string, string> = {
  catalog_basic: 'bookabilityCatalogBasic',
  catalog_conditional: 'bookabilityCatalogConditional',
  diagnostic: 'bookabilityDiagnostic',
  complex_or_parts: 'bookabilityComplex',
  safety_sensitive: 'bookabilitySafety',
  commercial_exception: 'bookabilityCommercial',
};

const URGENCY_LABEL_KEYS: Record<string, string> = {
  low: 'urgencyLow',
  medium: 'urgencyMedium',
  high: 'urgencyHigh',
  unknown: 'urgencyUnknown',
};

export interface IntakeReviewActionPayload {
  action: 'claim' | 'ask_customer' | 'close' | 'block';
  questions?: string[];
  note?: string;
}

interface IntakeReviewCardProps {
  conversation: Conversation;
  onAction: (payload: IntakeReviewActionPayload) => Promise<void>;
  busy?: boolean;
}

function hasIntakeContent(summary: IntakeSummary | undefined): boolean {
  if (!summary) return false;
  return Boolean(
    summary.vehicleRegNumber ||
      summary.vehicleMakeModel ||
      summary.symptom ||
      summary.proposedWorkType ||
      summary.bookability ||
      summary.workshopSummary,
  );
}

export function IntakeReviewCard({ conversation, onAction, busy = false }: IntakeReviewCardProps) {
  const { t } = useTranslation('messages');
  const { t: tc } = useTranslation('common');
  const inQueue = REVIEW_STATUSES.has(conversation.staffQueueStatus ?? '');
  const summary = conversation.intakeSummary;
  const [askOpen, setAskOpen] = useState(false);
  const [askText, setAskText] = useState('');
  const [closeOpen, setCloseOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockNote, setBlockNote] = useState('');

  if (!inQueue && !hasIntakeContent(summary)) {
    return null;
  }

  const rows: Array<{ label: string; value: string }> = [];
  if (summary?.vehicleRegNumber || summary?.vehicleMakeModel) {
    rows.push({
      label: t('intakeVehicle'),
      value: [summary.vehicleRegNumber, summary.vehicleMakeModel].filter(Boolean).join(' · '),
    });
  }
  if (summary?.symptom) rows.push({ label: t('intakeSymptom'), value: summary.symptom });
  if (summary?.urgency) {
    rows.push({
      label: t('intakeUrgency'),
      value: t(URGENCY_LABEL_KEYS[summary.urgency] ?? 'urgencyUnknown'),
    });
  }
  if (summary?.safetyNotes) rows.push({ label: t('intakeSafety'), value: summary.safetyNotes });
  if (summary?.proposedWorkType) rows.push({ label: t('intakeWorkType'), value: summary.proposedWorkType });
  if (summary?.bookability) {
    rows.push({
      label: t('intakeBookability'),
      value: t(BOOKABILITY_LABEL_KEYS[summary.bookability] ?? 'bookabilityDiagnostic'),
    });
  }
  if (summary?.desiredTime) rows.push({ label: t('intakeDesiredTime'), value: summary.desiredTime });
  if (summary?.workshopSummary) rows.push({ label: t('intakeSummary'), value: summary.workshopSummary });

  const queueLabel =
    conversation.staffQueueStatus === 'claimed'
      ? t('queueClaimed')
      : conversation.staffQueueStatus === 'needs_info'
        ? t('queueNeedsInfo')
        : conversation.staffQueueStatus === 'blocked'
          ? t('queueBlocked')
          : conversation.staffQueueStatus === 'decision_ready'
            ? t('queueDecisionReady')
            : t('queueQueued');

  const submitAsk = async () => {
    const questions = askText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (questions.length === 0) return;
    await onAction({ action: 'ask_customer', questions });
    setAskOpen(false);
    setAskText('');
  };

  return (
    <>
      <Accordion
        defaultExpanded={conversation.staffQueueStatus === 'queued'}
        disableGutters
        sx={{ borderBottom: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {t('intakeCardTitle')}
            </Typography>
            {inQueue && (
              <Chip
                size="small"
                color={conversation.staffQueueStatus === 'blocked' ? 'warning' : 'info'}
                label={queueLabel}
                sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.7rem' } }}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('intakeEmpty')}
            </Typography>
          ) : (
            <Stack spacing={0.75} sx={{ mb: 1.5 }}>
              {rows.map((row) => (
                <Box key={row.label}>
                  <Typography variant="caption" color="text.secondary">
                    {row.label}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
          {inQueue && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {conversation.staffQueueStatus === 'queued' && (
                <Button size="small" variant="contained" disabled={busy} onClick={() => { void onAction({ action: 'claim' }); }}>
                  {t('claimReview')}
                </Button>
              )}
              <Button size="small" disabled={busy} onClick={() => setAskOpen(true)}>
                {t('askCustomer')}
              </Button>
              <Button size="small" disabled={busy} onClick={() => setBlockOpen(true)}>
                {t('blockReview')}
              </Button>
              <Button size="small" disabled={busy} onClick={() => setCloseOpen(true)}>
                {t('closeReview')}
              </Button>
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <ResponsiveDialog
        open={askOpen}
        onClose={() => setAskOpen(false)}
        maxWidth="sm"
        fullWidth
        mobileTitle={t('askCustomer')}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('askCustomer')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('askCustomerQuestions')}
            helperText={t('askCustomerHint')}
            value={askText}
            onChange={(event) => setAskText(event.target.value)}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setAskOpen(false)}>{tc('cancel')}</Button>
            <Button variant="contained" disabled={busy || !askText.trim()} onClick={() => { void submitAsk(); }}>
              {t('askCustomerSend')}
            </Button>
          </Stack>
        </Box>
      </ResponsiveDialog>

      <ConfirmDialog
        open={closeOpen}
        title={t('closeReview')}
        message={t('closeReviewHint')}
        onCancel={() => setCloseOpen(false)}
        onConfirm={() => {
          setCloseOpen(false);
          void onAction({ action: 'close' });
        }}
        loading={busy}
      />

      <ResponsiveDialog
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        maxWidth="sm"
        fullWidth
        mobileTitle={t('blockReview')}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('blockReview')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t('blockReviewNote')}
            value={blockNote}
            onChange={(event) => setBlockNote(event.target.value)}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button onClick={() => setBlockOpen(false)}>{tc('cancel')}</Button>
            <Button
              variant="contained"
              color="warning"
              disabled={busy || !blockNote.trim()}
              onClick={() => {
                void onAction({ action: 'block', note: blockNote.trim() });
                setBlockOpen(false);
                setBlockNote('');
              }}
            >
              {t('blockReview')}
            </Button>
          </Stack>
        </Box>
      </ResponsiveDialog>
    </>
  );
}
